import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BrainCircuit, Globe, Zap, ListChecks, 
  ShieldCheck, X, Save, RotateCcw 
} from 'lucide-react';

const AISettingsModal = ({ isOpen, onClose, onSave, currentSettings, orgId }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // حالة الإعدادات المحلية داخل المودال
  const [settings, setSettings] = useState({
    org_id: orgId,
    enable_predictive: false,
    target_language: 'en',
    confidence_threshold: 85,
    auto_create_tasks: true,
    max_tasks_count: 5
  });

  // تحديث البيانات لو القادمة من الأب (Parent) تغيرت
  useEffect(() => {
    // 🎯 التعديل هنا: نحدث الـ state فقط لو currentSettings موجودة فعلاً والـ Modal مفتوح
    if (isOpen && currentSettings) {
      console.log(currentSettings);
      setSettings({
        ...currentSettings,
        org_id: orgId, // نضمن بقاء الـ orgId
        // نضمن تحويل أي قيمة (t/f) لـ Boolean حقيقي لـ React
        enable_predictive: !!currentSettings.enable_predictive,
        auto_create_tasks: !!currentSettings.auto_create_tasks
      });
    }
  }, [currentSettings, isOpen, orgId]);

  if (!isOpen) return null;

  const languages = [
    { code: 'ar', name: 'العربية' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' },
    { code: 'it', name: 'Italiano' },
    { code: 'ja', name: '日本語' },
    { code: 'ko', name: '한국어' },
    { code: 'ru', name: 'Русский' },
    { code: 'zh', name: '中文' },
    { code: 'ur', name: 'اُردُو' }
  ];

  return (
    <div className={`fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[999] flex items-center justify-center p-4 transition-all ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-300">
        
        {/* Header - الهيدر الاحترافي */}
        <div className="bg-[#1a3353] p-6 text-white flex justify-between items-center relative">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/30 rounded-2xl backdrop-blur-sm border border-white/10">
              <BrainCircuit size={28} className="text-blue-300 animate-pulse" />
            </div>
            <div>
              <h2 className="text-xl font-black tracking-tight">{t('ai_settings.title')}</h2>
              <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
                {t('ai_settings.sub_title')}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/70 hover:text-white"
          >
            <X size={24}/>
          </button>
        </div>

        <div className="p-8 space-y-8">
          
          {/* Activation Section - تفعيل الصيانة التنبؤية */}
          <div className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all ${settings.enable_predictive ? 'bg-blue-50 border-blue-200 shadow-inner' : 'bg-slate-50 border-slate-100'}`}>
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${settings.enable_predictive ? 'bg-blue-600 text-white' : 'bg-slate-300 text-slate-500'}`}>
                <Zap size={20} />
              </div>
              <div>
                <p className="font-black text-slate-900 text-sm">{t('ai_settings.enable_label')}</p>
                <p className="text-[10px] text-slate-500 font-bold uppercase italic tracking-wider">
                  {t('ai_settings.enable_sub')}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer scale-110">
              <input 
                type="checkbox" 
                checked={Boolean(settings.enable_predictive)} 
                onChange={(e) => setSettings({...settings, enable_predictive: e.target.checked})} 
                className="sr-only peer" 
              />
              <div className="w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Language - اختيار اللغة */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-slate-500 uppercase flex items-center gap-2 tracking-widest">
                <Globe size={14} className="text-blue-500" /> {t('ai_settings.target_lang')}
              </label>
              <div className="relative">
                <select 
                  value={settings.target_language} 
                  onChange={(e) => setSettings({...settings, target_language: e.target.value})}
                  className="w-full bg-slate-50 border-2 border-slate-100 p-3.5 rounded-xl font-bold text-sm outline-none focus:border-blue-500 appearance-none cursor-pointer transition-all"
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
                <div className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${isRTL ? 'left-4' : 'right-4'}`}>
                  <Globe size={16} className="text-slate-400" />
                </div>
              </div>
            </div>

            {/* Threshold - حد الثقة */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-slate-500 uppercase flex items-center gap-2 tracking-widest">
                <ShieldCheck size={14} className="text-green-500" /> {t('ai_settings.threshold')}
              </label>
              <div className="relative">
                <input 
                  type="number" 
                  min="50" max="100"
                  value={settings.confidence_threshold} 
                  onChange={(e) => setSettings({...settings, confidence_threshold: parseInt(e.target.value)})}
                  className="w-full bg-slate-50 border-2 border-slate-100 p-3.5 rounded-xl font-bold text-sm focus:border-blue-500 outline-none transition-all"
                />
                <span className={`absolute top-1/2 -translate-y-1/2 font-black text-slate-400 ${isRTL ? 'left-4' : 'right-4'}`}>%</span>
              </div>
            </div>

            {/* Auto Task Generation - توليد المهام */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-slate-500 uppercase flex items-center gap-2 tracking-widest">
                <ListChecks size={14} className="text-purple-500" /> {t('ai_settings.auto_tasks')}
              </label>
              <div className="flex items-center gap-4 p-3.5 bg-slate-50 rounded-xl border-2 border-slate-100">
                <input 
                  type="checkbox" 
                  checked={settings.auto_create_tasks} 
                  onChange={(e) => setSettings({...settings, auto_create_tasks: e.target.checked})} 
                  className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500 transition-all cursor-pointer" 
                />
                <span className="text-[11px] font-bold text-slate-600 italic uppercase tracking-tighter">
                  {t('ai_settings.task_desc')}
                </span>
              </div>
            </div>

            {/* Max Tasks Count - أقصى عدد للمهام */}
            <div className="space-y-3">
              <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                {t('ai_settings.max_tasks')}
              </label>
              <input 
                type="number" 
                min="1" max="15"
                value={settings.max_tasks_count} 
                onChange={(e) => setSettings({...settings, max_tasks_count: parseInt(e.target.value)})}
                className="w-full bg-slate-50 border-2 border-slate-100 p-3.5 rounded-xl font-bold text-sm focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>
        </div>

        {/* Footer - أزرار التحكم */}
        <div className={`p-6 bg-slate-50 flex justify-end gap-4 border-t border-slate-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button 
            onClick={onClose} 
            className="flex items-center gap-2 px-6 py-2.5 text-xs font-black text-slate-400 hover:text-slate-700 uppercase italic transition-all"
          >
            <RotateCcw size={14}/> {t('common.cancel')}
          </button>
          <button 
            onClick={() => onSave(settings)} 
            className="flex items-center gap-2 bg-[#1a3353] text-white px-10 py-3 rounded-xl shadow-xl font-black text-xs uppercase hover:bg-black hover:-translate-y-1 transition-all active:scale-95"
          >
            <Save size={16}/> {t('common.save_settings')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AISettingsModal;